import { createCpuTilePresentation } from './cpuTilePresentation';
import { createRenderBudget } from './renderBudget';
import { renderFractalCpuTiles, renderFractalCpuRegion } from './fractalCPUProgressive';

const sameRanges = (a, b) => !!a && !!b && ['xRange', 'yRange'].every(axis =>
  a[axis]?.[0] === b[axis]?.[0] && a[axis]?.[1] === b[axis]?.[1]);
const paramsKey = params => JSON.stringify(Object.keys(params || {}).sort()
  .map(key => [key, params[key]]));
const frozenRanges = ranges => Object.freeze({
  xRange: Object.freeze([...ranges.xRange]), yRange: Object.freeze([...ranges.yRange]) });
const defaultNow = () => performance.now();
// Two visible canvases each need latest-anchor and useful-progress work within
// two 120Hz frames. Four 1ms quanta fit that shared window; 2ms quanta do not.
const CPU_SLICE_MS = 1;

/**
 * The visible canvas follows the newest camera on RAF. CPU computation owns
 * immutable grids: useful full refinement continues while a separate small
 * anchor request follows input. Only original sources become history frames.
 */
export function createCpuMotionLoop(canvas, { renderFull, renderTile, cancelRender,
  shouldUseWorker, colorFunc, paletteParams = null, cpuReusePaletteArray = false,
  cpuInteriorDispatch = true, initialSource, onFrame, onError, isVisible, now = defaultNow,
  createPresenter = createCpuTilePresentation, createBudget = createRenderBudget,
  requestFrame = callback => requestAnimationFrame(callback),
  cancelFrame = id => cancelAnimationFrame(id) } = {}) {
  let presenter, budget;
  try {
    presenter = createPresenter(canvas);
    if (presenter) budget = createBudget(Symbol('cpu-motion'));
  } catch (error) { presenter?.dispose?.(); return null; }
  if (!presenter) return null;
  let latest = null, fullJob = null, anchorJob = null, completed = null, source = null;
  let lastAnchor = null, frame = null, epoch = 0, disposed = false, suspended = false;
  let width = canvas.width, height = canvas.height, calibration = 6;
  let fullStarted = 0, fullCompleted = 0, anchorStarted = 0, acceptedTiles = 0;
  let discardedTiles = 0, admissionLimited = false, lastPresentedAt = null;
  let conservativeChargedMs = 0, requestVersion = 0, workerFallbacks = 0;
  let externalPresentationPending = false;
  let lastExternalPresentationTime = null;

  const visible = () => !disposed && !suspended && !document.hidden &&
    (isVisible ? isVisible(canvas) : true);
  const currentSource = () => source && latest && source.width === canvas.width &&
    source.height === canvas.height && source.colorKey === latest.colorKey &&
    sameRanges(source.ranges, latest.ranges);
  const currentJob = job => !!job && !disposed && job.epoch === epoch &&
    !job.controller.signal.aborted &&
    job.width === canvas.width && job.height === canvas.height &&
    job.colorKey === latest?.colorKey;

  function stats() {
    const presentation = presenter.stats || {};
    const assemblyBytes = (fullJob ? fullJob.width * fullJob.height * 4 : 0) +
      (anchorJob ? anchorJob.tile.width * anchorJob.tile.height * 4 : 0) +
      (completed && !completed.admitted ? completed.width * completed.height * 4 : 0);
    return { renderer: 'progressive-cpu', ...budget.snapshot(), ...presentation,
      fullPending: !!fullJob, anchorPending: !!anchorJob, completedPending: !!completed,
      fullStarted, fullCompleted, anchorStarted, acceptedWorkerTiles: acceptedTiles,
      workerFallbacks,
      discardedWorkerTiles: discardedTiles, admissionLimited, assemblyBytes,
      calibrationFramesRemaining: calibration,
      totalResidentBytes: (presentation.residentBytes || 0) + assemblyBytes,
      sourceCameraAgeKnown: source?.cameraRequestedAtKnown !== false &&
        (!completed?.seed || completed.cameraRequestedAtKnown !== false),
      requestedRanges: latest?.ranges ?? null,
      requestedCameraAt: latest?.cameraRequestedAt ?? null, presentedAt: lastPresentedAt,
      timingNote: 'CPU slices and installation time are measured; RAF is not display latency.' };
  }

  function record() { canvas._fractalMotionStats = stats(); }
  function scheduleFrame() {
    if (frame !== null || externalPresentationPending || !visible() || !latest) return;
    frame = requestFrame(presentFrame);
  }
  function accountSlice(job, measuredMs) {
    if (job.sliceStarted === null || job.worker) return;
    const cost = measuredMs === undefined ? Math.max(0, now() - job.sliceStarted) : measuredMs;
    const overrun = Math.max(0, cost - (job.reservedMs || 0));
    if (overrun) budget.consumeCompute(overrun, { kind: job.kind });
    job.reservedMs = 0;
    job.sliceStarted = null;
  }
  function cancelWork() {
    epoch++;
    for (const job of [fullJob, anchorJob]) {
      job?.controller?.abort();
      // Resolve a parked yield so the helper can observe AbortSignal and free
      // its arrays. Promise resolution does not admit more numerical work.
      job?.resume?.();
    }
    fullJob = anchorJob = null;
    cancelRender?.();
  }
  function hardInvalidate(clearSource) {
    cancelWork();
    completed = null;
    lastAnchor = null;
    admissionLimited = false;
    if (clearSource) { presenter.reset(); source = null; }
  }
  function fail(error) {
    if (disposed) return;
    try { api.suspend(); } catch (restoreError) { /* The compositor itself may be lost. */ }
    api.dispose();
    onError?.(error);
  }
  function acceptTile(job, tile) {
    if (!currentJob(job)) { discardedTiles++; return; }
    const input = { ...tile, colorKey: job.colorKey,
      cameraRequestedAt: job.cameraRequestedAt,
      priority: job.kind === 'latest',
      snapshot: { ...tile.snapshot, colorKey: job.colorKey } };
    if (presenter.queueTile(input)) acceptedTiles++;
    else discardedTiles++;
    scheduleFrame();
  }
  function makeJob(kind, tile) {
    return { epoch, kind, width: canvas.width, height: canvas.height,
      ranges: latest.ranges, colorParams: latest.colorParams, colorKey: latest.colorKey,
      cameraRequestedAt: latest.cameraRequestedAt, tile, worker: false,
      controller: new AbortController(), resume: null, sliceStarted: null, reservedMs: 0,
      startedQuantum: false };
  }
  function fallbackOptions(job) {
    return { width: job.width, height: job.height, ranges: job.ranges, colorFunc,
      useMu: job.colorParams?.useMu !== false,
      paletteParams: cpuReusePaletteArray ? { ...(paletteParams || {}), ...job.colorParams } : null,
      signal: job.controller.signal, now,
      budgetMs: Math.max(0.1, Math.min(CPU_SLICE_MS, budget.snapshot().submissionBudgetMs)),
      cameraRequestedAt: job.cameraRequestedAt,
      onSliceStart: start => { job.sliceStarted = start; },
      onSliceEnd: duration => accountSlice(job, duration),
      yieldTask: () => {
        accountSlice(job);
        return new Promise(resolve => {
          // Actual slice timing starts inside the helper's resumed execution,
          // after the microtask wait; it excludes other canvases' queued work.
          job.resume = () => { job.resume = null; resolve(); };
          if (job.controller.signal.aborted) job.resume();
          else scheduleFrame();
        });
      } };
  }
  function handleFailure(job, error) {
    if (!currentJob(job)) return;
    if (error?.name === 'AbortError') {
      if (fullJob === job) fullJob = null;
      if (anchorJob === job) anchorJob = null;
      scheduleFrame();
      return;
    }
    if (job.worker) {
      // Worker startup/transport failure changes computation capability, not
      // the camera or accepted coverage. Continue this immutable job in bounded
      // main-thread slices rather than abandoning progressive presentation.
      workerFallbacks++;
      job.worker = false;
      job.reservedMs = 0;
      job.resume = () => {
        job.resume = null;
        if (!job.controller.signal.aborted) startFallback(job);
      };
      scheduleFrame();
      return;
    }
    fail(error);
  }
  function emitFrame(presentation) {
    source.notifiedCurrent = !!presentation.exactCurrent;
    onFrame?.({ ranges: source.ranges, width: source.width, height: source.height,
      pixels: source.pixels, colorParams: source.colorParams,
      cameraRequestedAt: source.cameraRequestedAt, current: source.notifiedCurrent,
      worker: !!source.worker,
      sourceKind: source.worker ? 'worker' : 'main-thread',
      cameraRequestedAtKnown: source.cameraRequestedAtKnown !== false,
      sourceTimeKnown: source.cameraRequestedAtKnown !== false,
      stats: { ...presentation } });
  }
  function completeFull(job, pixels) {
    accountSlice(job);
    if (!currentJob(job)) return;
    fullJob = null;
    fullCompleted++;
    // Returning to an existing exact source must not replace it with an
    // older, projected completed job that no longer improves this camera.
    if (!currentSource() || sameRanges(job.ranges, latest.ranges)) {
      completed = { ...job, pixels, admitted: false };
    }
    scheduleFrame();
  }
  function completeAnchor(job, tile) {
    accountSlice(job);
    if (!currentJob(job)) return;
    anchorJob = null;
    lastAnchor = job;
    acceptTile(job, tile);
    scheduleFrame();
  }
  function startFallback(job) {
    job.startedQuantum = true;
    job.sliceStarted = now();
    let promise;
    try {
      promise = job.kind === 'progress'
        ? renderFractalCpuTiles({ ...fallbackOptions(job), onTile: tile => acceptTile(job, tile) })
        : renderFractalCpuRegion({ ...fallbackOptions(job), tile: job.tile,
          onTile: tile => acceptTile(job, tile) });
      accountSlice(job);
    } catch (error) { handleFailure(job, error); return; }
    Promise.resolve(promise).then(result => job.kind === 'progress'
      ? completeFull(job, result) : completeAnchor(job, result),
    error => { accountSlice(job); handleFailure(job, error); });
  }
  function startFull(reservedMs) {
    const job = makeJob('progress');
    fullJob = job;
    fullStarted++;
    job.reservedMs = reservedMs;
    job.worker = !!renderFull && !!shouldUseWorker?.(job.width, job.height);
    const start = now();
    let promise;
    try {
      if (job.worker) {
        promise = renderFull({ canvasWidth: job.width, canvasHeight: job.height,
          ...job.ranges, colorParams: job.colorParams, cpuInteriorDispatch,
          cameraRequestedAt: job.cameraRequestedAt, onTile: tile => acceptTile(job, tile) });
        const overrun = Math.max(0, now() - start - reservedMs);
        if (overrun) budget.consumeCompute(overrun, { kind: 'progress' });
      } else {
        job.startedQuantum = true;
        job.sliceStarted = start;
        promise = renderFractalCpuTiles({ ...fallbackOptions(job),
          onTile: tile => acceptTile(job, tile) });
        accountSlice(job);
      }
    } catch (error) { handleFailure(job, error); return; }
    Promise.resolve(promise).then(pixels => completeFull(job, pixels),
      error => { accountSlice(job); handleFailure(job, error); });
  }
  function anchorTile() {
    const tileWidth = Math.min(64, canvas.width), tileHeight = Math.min(32, canvas.height);
    const x = Number.isFinite(latest.anchor?.x) ? latest.anchor.x : canvas.width / 2;
    const y = Number.isFinite(latest.anchor?.y) ? latest.anchor.y : canvas.height / 2;
    return { x: Math.max(0, Math.min(canvas.width - tileWidth, Math.round(x - tileWidth / 2))),
      y: Math.max(0, Math.min(canvas.height - tileHeight, Math.round(y - tileHeight / 2))),
      width: tileWidth, height: tileHeight };
  }
  function needsAnchor() {
    if (!latest?.interactive || currentSource()) return false;
    const tile = anchorTile();
    return !lastAnchor || !sameRanges(lastAnchor.ranges, latest.ranges) ||
      lastAnchor.tile.x !== tile.x || lastAnchor.tile.y !== tile.y;
  }
  function startAnchor(reservedMs) {
    const job = makeJob('latest', anchorTile());
    anchorJob = job;
    anchorStarted++;
    job.reservedMs = reservedMs;
    job.worker = !!renderTile && !!shouldUseWorker?.(job.width, job.height);
    const start = now();
    let promise;
    try {
      if (job.worker) {
        promise = renderTile({ canvasWidth: job.width, canvasHeight: job.height,
          ...job.ranges, colorParams: job.colorParams, tile: job.tile,
          cameraRequestedAt: job.cameraRequestedAt });
        const overrun = Math.max(0, now() - start - reservedMs);
        if (overrun) budget.consumeCompute(overrun, { kind: 'latest' });
      } else {
        job.startedQuantum = true;
        job.sliceStarted = start;
        promise = renderFractalCpuRegion({ ...fallbackOptions(job), tile: job.tile,
          onTile: tile => acceptTile(job, tile) });
        accountSlice(job);
      }
    } catch (error) { handleFailure(job, error); return; }
    Promise.resolve(promise).then(tile => completeAnchor(job, tile),
      error => { accountSlice(job); handleFailure(job, error); });
  }

  function admissionSignature() {
    const state = presenter.stats || {};
    return [requestVersion, state.residentBytes, state.pendingUploads,
      state.uploadedPixels, state.acceptedTiles].join(':');
  }
  function needsSourceAdmission() {
    return !!completed && !completed.admitted &&
      completed.admissionBlocked !== admissionSignature();
  }

  function retargetAnchorAtBoundary() {
    const job = anchorJob;
    if (!job || job.worker || !job.resume || !job.startedQuantum) return;
    const tile = anchorTile();
    if (sameRanges(job.ranges, latest.ranges) && job.tile.x === tile.x && job.tile.y === tile.y) return;
    // A main-thread anchor has already received its bounded quantum. Keep its
    // accepted original microtiles, then reserve this channel for the newest
    // camera; the useful full refinement has its independent finite path.
    job.controller.abort();
    const resume = job.resume;
    anchorJob = null;
    resume();
  }

  function admit(kind) {
    const job = kind === 'latest' ? anchorJob : fullJob;
    const canStart = kind === 'latest' ? (!anchorJob && needsAnchor())
      : (!fullJob && !completed && !currentSource());
    if (!job?.resume && !canStart) return;
    const willUseWorker = !job?.resume && !!(kind === 'latest' ? renderTile : renderFull) &&
      !!shouldUseWorker?.(canvas.width, canvas.height);
    const estimateMs = willUseWorker ? 0.1 : Math.min(CPU_SLICE_MS, budget.snapshot().submissionBudgetMs);
    // Until idle refresh calibration succeeds, total local main-thread work
    // starts conservatively at 2ms rather than assuming a 60Hz display.
    if (calibration > 0 && conservativeChargedMs + estimateMs > 2) return;
    if (!budget.canSubmit({ kind, estimateMs })) return;
    budget.consumeCompute(estimateMs, { kind });
    conservativeChargedMs += estimateMs;
    if (job?.resume) { job.reservedMs = estimateMs; job.resume(); }
    else if (!job) {
      if (kind === 'latest' && needsAnchor()) startAnchor(estimateMs);
      else if (kind === 'progress' && !completed && !currentSource()) startFull(estimateMs);
    }
  }
  function presentFrame(time) {
    frame = null;
    if (!visible() || !latest) {
      if (!disposed && !suspended && (fullJob || anchorJob)) cancelWork();
      budget.release();
      return;
    }
    try {
      if (width !== canvas.width || height !== canvas.height) {
        width = canvas.width; height = canvas.height;
        hardInvalidate(false);
      }
      retargetAnchorAtBoundary();
      const pending = {
        latest: !!anchorJob?.resume || (!anchorJob && needsAnchor()),
        progress: !!fullJob?.resume || (!fullJob && !completed && !currentSource()) };
      budget.tick(time, { visible: true, interactive: latest.interactive,
        active: latest.interactive, pending });
      conservativeChargedMs = 0;
      if (needsSourceAdmission()) {
        completed.admitted = presenter.setSource({ pixels: completed.pixels,
          width: completed.width, height: completed.height, ...completed.ranges,
          colorKey: completed.colorKey, cameraRequestedAt: completed.cameraRequestedAt });
        admissionLimited = !completed.admitted;
        completed.admissionBlocked = completed.admitted ? null : admissionSignature();
      }
      const installBudget = budget.snapshot().remainingInstallMs;
      const seed = completed?.seed && !source ? completed : null;
      const presentation = presenter.present(latest.ranges, {
        budgetMs: budget.canInstall(0) ? installBudget : 0, now,
        colorKey: latest.colorKey, cameraRequestedAt: latest.cameraRequestedAt,
        anchor: latest.anchor, preserveCanvasUntilSourceReady: !!seed });
      if (!presentation) throw new Error('CPU presentation failed');
      budget.consumeInstall(presentation.lastInstallMs || 0);
      lastPresentedAt = time;
      if (canvas.style) {
        if (seed && !presentation.sourceReady) {
          const spanX = latest.ranges.xRange[1] - latest.ranges.xRange[0];
          const spanY = latest.ranges.yRange[1] - latest.ranges.yRange[0];
          const scaleX = (seed.ranges.xRange[1] - seed.ranges.xRange[0]) / spanX;
          const scaleY = (seed.ranges.yRange[1] - seed.ranges.yRange[0]) / spanY;
          const dx = (seed.ranges.xRange[0] - latest.ranges.xRange[0]) / spanX *
            (canvas.clientWidth || canvas.width);
          const dy = (seed.ranges.yRange[0] - latest.ranges.yRange[0]) / spanY *
            (canvas.clientHeight || canvas.height);
          canvas.style.transformOrigin = '0 0';
          canvas.style.transform = `translate(${dx}px, ${dy}px) scale(${scaleX}, ${scaleY})`;
        } else canvas.style.transform = '';
      }
      if (completed?.admitted && presentation.sourceReady && !presentation.pendingSource &&
          presentation.sourceCameraRequestedAt === completed.cameraRequestedAt &&
          sameRanges(presentation.sourceRanges, completed.ranges)) {
        source = completed;
        completed = null;
        emitFrame(presentation);
      }
      if (source && presentation.exactCurrent && source.notifiedCurrent !== true) emitFrame(presentation);
      // Source promotion can expose a newer refinement request in this same
      // display interval. Updating pending kinds preserves the existing ledger
      // capacity while allowing that bounded next job to be admitted now.
      budget.tick(time, { visible: true, interactive: latest.interactive,
        active: latest.interactive, pending: {
          latest: !!anchorJob?.resume || (!anchorJob && needsAnchor()),
          progress: !!fullJob?.resume || (!fullJob && !completed && !currentSource()) } });
      if (calibration > 0 && !latest.interactive) calibration--;
      else {
        const firstKind = budget.snapshot().dueKind || 'latest';
        admit(firstKind);
        admit(firstKind === 'latest' ? 'progress' : 'latest');
      }
      record();
      const hasWork = !!fullJob || !!anchorJob || !!completed?.admitted || needsSourceAdmission() ||
        !!presenter.stats?.pendingUploads || (!completed && !currentSource() && !!latest);
      if (hasWork) scheduleFrame();
      else if (!latest.interactive && calibration > 0) scheduleFrame();
    } catch (error) { fail(error); }
  }

  const api = {
    reservePresentation() {
      if (disposed) return;
      externalPresentationPending = true;
      if (frame !== null) cancelFrame(frame);
      frame = null;
    },
    request(ranges, colorParams, options = {}) {
      if (disposed) return false;
      externalPresentationPending = false;
      const colorKey = paramsKey(colorParams);
      const changed = !latest || !sameRanges(latest.ranges, ranges) ||
        latest.colorKey !== colorKey || width !== canvas.width || height !== canvas.height;
      if (changed) requestVersion++;
      if (latest && latest.colorKey !== colorKey) hardInvalidate(true);
      if (!latest && completed?.seed && completed.colorKey !== colorKey) hardInvalidate(true);
      if (width !== canvas.width || height !== canvas.height) {
        width = canvas.width; height = canvas.height;
        hardInvalidate(false);
      }
      suspended = false;
      latest = { ranges: frozenRanges(ranges),
        colorParams: Object.freeze({ ...(colorParams || {}) }), colorKey,
        cameraRequestedAt: changed ? (options.cameraRequestedAt ?? now())
          : latest.cameraRequestedAt,
        interactive: !!options.interactive,
        anchor: options.anchor ? { ...options.anchor } : null };
      // The canvas interaction owner already coalesces input onto RAF. Compose
      // in that same opportunity instead of adding a second frame of latency.
      // A later render/visibility poll may reuse its options. Replaying that
      // old timestamp would rewind the shared budget clock for every canvas.
      const freshPresentationTime = Number.isFinite(options.presentationTime) &&
        options.presentationTime !== lastExternalPresentationTime &&
        (lastPresentedAt === null || options.presentationTime >= lastPresentedAt);
      if (freshPresentationTime && visible()) {
        lastExternalPresentationTime = options.presentationTime;
        if (frame !== null) cancelFrame(frame);
        frame = null;
        presentFrame(options.presentationTime);
      } else scheduleFrame();
      return true;
    },
    suspend() {
      if (disposed) return;
      suspended = true;
      externalPresentationPending = false;
      cancelWork();
      if (frame !== null) cancelFrame(frame);
      frame = null;
      budget.release();
      if (source) presenter.present(source.ranges, { budgetMs: 0, now,
        colorKey: source.colorKey, sourceOnly: true });
      else if (completed?.seed && canvas.style) canvas.style.transform = '';
      record();
    },
    resume() { if (!disposed) { suspended = false; externalPresentationPending = false; scheduleFrame(); } },
    dispose() {
      if (disposed) return;
      disposed = true;
      cancelWork();
      if (frame !== null) cancelFrame(frame);
      frame = null;
      completed = source = latest = null;
      presenter.dispose();
      budget.dispose();
      document.removeEventListener('visibilitychange', visibilityChanged);
      record();
    },
    get presenter() { return presenter; },
    get stats() { return stats(); },
  };
  function visibilityChanged() {
    if (disposed) return;
    if (document.hidden) {
      // Document pausing must not overwrite an explicit renderer handoff.
      // Retire owned work, then wait for a visible request or visibility return.
      cancelWork();
      if (frame !== null) cancelFrame(frame);
      frame = null;
      budget.release();
      record();
    } else if (!suspended) scheduleFrame();
  }
  document.addEventListener('visibilitychange', visibilityChanged);
  if (initialSource?.pixels && initialSource.width > 0 && initialSource.height > 0 &&
      initialSource.xRange && initialSource.yRange) {
    completed = { ranges: frozenRanges(initialSource), width: initialSource.width,
      height: initialSource.height, pixels: initialSource.pixels,
      colorParams: Object.freeze({ ...(initialSource.colorParams || {}) }),
      colorKey: paramsKey(initialSource.colorParams),
      // A legacy exact cache may lack latch time. Zero is a conservative age
      // estimate relative to navigation start, never a fresh installation time.
      cameraRequestedAt: initialSource.cameraRequestedAt ?? 0,
      cameraRequestedAtKnown: Number.isFinite(initialSource.cameraRequestedAt) &&
        initialSource.cameraRequestedAtKnown !== false && initialSource.sourceTimeKnown !== false,
      admitted: false, seed: true,
      worker: initialSource.worker !== false };
  }
  return api;
}
